import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Heart, Copy, Check, Smartphone } from "lucide-react";
import { toast } from "sonner";

const SWISH_NUMBER = "0707691184";
const DEFAULT_MSG = "Stöd Manuell Medicin";
const PRESETS = [50, 100, 200, 500];

function buildSwishUrl(amount, msg) {
  const params = new URLSearchParams({
    sw: SWISH_NUMBER,
    cur: "SEK",
    msg: msg || DEFAULT_MSG,
    src: "qr",
  });
  if (amount && Number(amount) > 0) params.set("amt", String(amount));
  return `https://app.swish.nu/1/p/sw/?${params.toString()}`;
}

export default function SupportDialog({ open, onOpenChange }) {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [copied, setCopied] = useState(false);

  const effectiveAmount = custom ? Number(custom) : amount;
  const swishUrl = buildSwishUrl(effectiveAmount, DEFAULT_MSG);

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(SWISH_NUMBER);
      setCopied(true);
      toast.success("Swish-nummer kopierat");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kunde inte kopiera");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-card" data-testid="support-dialog">
        <DialogHeader>
          <div className="size-11 rounded-full bg-accent/15 text-accent flex items-center justify-center mb-2">
            <Heart className="size-5 fill-accent" />
          </div>
          <DialogTitle className="font-serif text-2xl tracking-tight">Stötta projektet</DialogTitle>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Manuell Medicin är ett oberoende utbildningsverktyg. Varje bidrag hjälper till att
            täcka AI-kostnader och att hålla appen kostnadsfri för studenter.
          </p>
        </DialogHeader>

        {/* Amount presets */}
        <div className="grid grid-cols-4 gap-2 mt-2" data-testid="support-presets">
          {PRESETS.map((p) => {
            const active = !custom && amount === p;
            return (
              <button
                key={p}
                onClick={() => { setAmount(p); setCustom(""); }}
                data-testid={`amount-${p}`}
                className={`py-2.5 rounded-md border text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card hover:bg-muted border-border"
                }`}
              >
                {p} kr
              </button>
            );
          })}
        </div>

        {/* Custom amount */}
        <div className="mt-1">
          <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5 block">
            Eller annat belopp
          </label>
          <div className="relative">
            <Input
              type="number"
              min="1"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="t.ex. 150"
              data-testid="custom-amount"
              className="pr-12 bg-card"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-muted-foreground">
              kr
            </span>
          </div>
        </div>

        {/* QR code – desktop / scan-mode */}
        <div className="mt-4 flex flex-col items-center gap-3 p-5 rounded-md bg-secondary/30 border border-dashed border-border">
          <div className="bg-white p-3 rounded-md" data-testid="swish-qr">
            <QRCodeSVG
              value={swishUrl}
              size={168}
              level="M"
              fgColor="#19395A"
              bgColor="#ffffff"
            />
          </div>
          <p className="text-xs text-center text-muted-foreground">
            Skanna QR-koden i Swish-appen
          </p>
        </div>

        {/* Mobile deeplink */}
        <a
          href={swishUrl}
          data-testid="swish-open-app"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors w-full mt-2"
        >
          <Smartphone className="size-4" /> Öppna i Swish-appen
          <span className="font-mono text-xs opacity-80">
            {effectiveAmount > 0 ? `${effectiveAmount} kr` : ""}
          </span>
        </a>

        {/* Number + copy */}
        <button
          onClick={copyNumber}
          data-testid="swish-copy-number"
          className="flex items-center justify-between w-full px-4 py-2.5 rounded-md bg-muted/40 hover:bg-muted transition-colors text-sm border border-border"
        >
          <span className="flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Swish-nr
            </span>
            <span className="font-mono font-medium">{SWISH_NUMBER}</span>
          </span>
          {copied ? <Check className="size-4 text-primary" /> : <Copy className="size-3.5 text-muted-foreground" />}
        </button>

        <p className="text-[11px] text-muted-foreground text-center mt-1">
          Tack för ditt bidrag! 💙
        </p>
      </DialogContent>
    </Dialog>
  );
}
